// interfaces
import {
    AutoPayFailedOrderActionPayload,
    NEWRELIC_TABLES,
    ProcessOrderActionPayload,
    getBridgeSdk,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    recordNREvent,
} from "@swiggy-private/checkout-core";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";

import {
    PaymentGroupDataDef,
    PaymentMethod,
    IUpiPaymentGroup,
    InAppUpiPaymentMethod,
    PaymentMethodDef,
    IInAppUpiPaymentGroup,
} from "@swiggy-private/payment-ui";
import {
    JuspayInAppUpiResponsePayload,
    JuspayInAppUpiRequestPayload,
    PayUsingInAppUpiResponsePayload,
} from "@swiggy-private/native-bridge";

// interfaces
import {
    VALID_PAYMENT_GROUP_TYPES,
    PaymentMethodMeta,
    VALID_PAYMENT_METHODS,
    PAYMENT_CODES,
    MARKETPLACE_CATEGORY,
} from "@swiggy-private/payment-types";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { CredOfferLayerDetailsInterface } from "@swiggy-private/native-bridge";

//constants
import { INAPP_UPI_STATE } from "@payment-client/constants/InAppUpi";
import { DEFAULT_JUSPAY_MERCHANTID } from "@payment-client/constants/Juspay";

//helpers
import { getPaymentMethodFromGroup, isUPIGroup } from "./PaymentHelper";
import { Some, Option, None } from "@swiggy-private/js-fp";
import { isEmpty } from "@swiggy-private/js-utils";
import { isVersionCodeEligible } from "@swiggy-private/core-ui";

export function isInAppUpiGroup(group: PaymentGroupDataDef): boolean {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.INAPP_UPI;
}

export function isUpiGroup(group: PaymentGroupDataDef): boolean {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.UPI;
}

export function getUpiGroup(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
): Option<IUpiPaymentGroup["paymentData"]> {
    return Some(paymentGroups.find(isUpiGroup));
}

export function getInAppUpiGroup(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
): Option<IInAppUpiPaymentGroup["paymentData"]> {
    return Some(paymentGroups.find(isInAppUpiGroup));
}

export type InAppUpiBridgeHandlerType = (
    payload: JuspayInAppUpiRequestPayload,
) => Promise<JuspayInAppUpiResponsePayload | undefined>;

export function isInAppUpiPaymentMethod(method: PaymentMethodDef): method is InAppUpiPaymentMethod {
    return method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.INAPP_UPI;
}

export function isInAppUpiMethodInOrderData(failedOrder: AutoPayFailedOrderActionPayload["failedOrder"]) {
    return failedOrder?.orderPaymentMethod === PAYMENT_CODES.INAPPUPI;
}
export function getNewInAppUpiPaymentMethod(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
    state: INAPP_UPI_STATE,
): Option<PaymentMethod<PaymentMethodMeta>> {
    const paymentGroup = paymentGroups.find((group) =>
        state === INAPP_UPI_STATE.ONBOARDING ? isUPIGroup(group) : isInAppUpiGroup(group),
    );
    if (paymentGroup == null) {
        return None;
    }
    return getPaymentMethodFromGroup(
        paymentGroup,
        state === INAPP_UPI_STATE.ONBOARDING
            ? VALID_PAYMENT_METHODS.NEW_INAPP_UPI_ONBOARDING
            : VALID_PAYMENT_METHODS.NEW_INAPP_UPI,
    );
}
export const getInAppUpiBridgeHandler = (inAppUpiState: INAPP_UPI_STATE): InAppUpiBridgeHandlerType | undefined => {
    const { addNewInAppUpi, initiateInAppUpiOnboarding } = getBridgeSdk();

    switch (inAppUpiState) {
        case INAPP_UPI_STATE.ADD_ACCOUNT:
            return addNewInAppUpi;
        case INAPP_UPI_STATE.ONBOARDING:
            return initiateInAppUpiOnboarding;
    }
};

export async function processInAppUpiBridgeRequest(
    payload: JuspayInAppUpiRequestPayload,
    type: INAPP_UPI_STATE,
): Promise<JuspayInAppUpiResponsePayload | undefined> {
    const bridgeHandler = getInAppUpiBridgeHandler(type);

    if (bridgeHandler == null) {
        return Promise.reject();
    }

    const inAppUpiBridgeResponse = await bridgeHandler(payload);
    if (inAppUpiBridgeResponse != null) {
        return Promise.resolve(inAppUpiBridgeResponse);
    }

    return Promise.reject();
}

export const getInAppUpiMethodData = (upiGroup: IUpiPaymentGroup["paymentData"]): InAppUpiPaymentMethod | undefined => {
    return upiGroup.paymentMethods.find(isInAppUpiPaymentMethod);
};

export function handlePayUsingInAppUpi(
    methodData: InAppUpiPaymentMethod,
    processOrderData: ProcessOrderActionPayload,
    paymentPartnerOrderId: string,
    params: {
        clientAuthToken: string;
        tenant: string;
        extendedCustomerId: string;
        credOfferLayerDetails?: CredOfferLayerDetailsInterface;
        merchantId?: string;
    },
): Promise<PayUsingInAppUpiResponsePayload | void> {
    const { referenceId } = methodData.meta;
    const { order: orderData, juspayMeta: paymentsJuspayMeta } = processOrderData;
    if (
        !isEmpty(referenceId) &&
        !isEmpty(params.extendedCustomerId) &&
        !isEmpty(orderData?.primaryPaymentTransactionAmount)
    ) {
        const payUsingInAppUpiPayload = {
            ...params,
            accountRefId: referenceId,
            orderId: paymentPartnerOrderId,
            orderAmount: String(orderData.primaryPaymentTransactionAmount),
            merchantId: params.merchantId ?? paymentsJuspayMeta?.merchantId ?? DEFAULT_JUSPAY_MERCHANTID,
        };

        if (!isEmpty(params.credOfferLayerDetails)) {
            payUsingInAppUpiPayload["credOfferLayerDetails"] = params.credOfferLayerDetails;
        }

        return getBridgeSdk().payUsingInAppUpi(payUsingInAppUpiPayload);
    } else {
        recordNREvent({
            eventType: NEWRELIC_TABLES.SWIGGY_UPI_PAYMENT_FAILURE,
            orderId: orderData.orderId,
            referenceId: referenceId || "",
            extendedCustomerId: params?.extendedCustomerId || "",
            primaryPaymentTransactionAmount: orderData?.primaryPaymentTransactionAmount || "",
        });
        return Promise.resolve();
    }
}

export async function isInAppUpiEnabled(tenant?: string): Promise<boolean> {
    const versionCode = GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode) || "";
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
    const MIN_ANDROID_VERSION = 1245;
    const MIN_IOS_VERSION = 521;
    const channel = GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) || "";
    const mcategory = (
        GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceCategory) || MARKETPLACE_CATEGORY.FOOD
    ).toLowerCase();
    if (!isEmpty(channel)) {
        return false;
    }

    if (mcategory !== MARKETPLACE_CATEGORY.FOOD && mcategory !== MARKETPLACE_CATEGORY.INSTAMART) {
        return false;
    }

    if (mcategory === MARKETPLACE_CATEGORY.INSTAMART) {
        const INAPP_DISABLED_ANDROID_VERSION = 1785;
        const INAPP_DISABLED_IOS_VERSION = 810;
        if (isVersionCodeEligible(INAPP_DISABLED_ANDROID_VERSION, INAPP_DISABLED_IOS_VERSION, versionCode, platform)) {
            return false;
        }
    }

    if (isVersionCodeEligible(MIN_ANDROID_VERSION, MIN_IOS_VERSION, versionCode, platform)) {
        return getBridgeSdk()
            .isInAppUpiEnabled({
                ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
            })
            .then((data) => {
                return !isEmpty(data) && data.isEnabled;
            })
            .catch((_err) => {
                return false;
            });
    } else {
        return false;
    }
}
